import React from 'react';
import './Scorecard.css';
import { formatGoalMetricValue, formatMetricValue, formatPercent } from '../utils/formatMetric';
import { getKpiStatusTone, getStatusIndicatorLabel } from '../utils/statusTone';
import ScorecardAttendance from './scorecard/ScorecardAttendance';
import ScorecardVacation from './scorecard/ScorecardVacation';
import KpiCard from './ui/KpiCard';
import DataTable from './ui/DataTable';
import SkeletonBlock from './ui/SkeletonBlock';

function Scorecard({
  manager,
  rollups,
  period,
  displayMode = 'print',
  title = 'Performance Scorecard',
  screenScorecardData = null,
  screenScorecardError = '',
  screenScorecardViewMode = 'mtd',
  onScreenScorecardViewModeChange = () => {},
  onManagerDrilldown = null,
  scorecardCardLimit = 4
}) {
  const periodLabel = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly'
  }[period];

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const getMissingTargetLabel = () => (
    screenScorecardViewMode === 'ytd' ? 'No yearly target set' : 'Target needed'
  );

  const getPendingReason = (row) => {
    if (!Number.isFinite(row.target)) return getMissingTargetLabel();
    if (row.result === null || row.result === undefined) return 'No data entered yet';
    return 'Not yet available';
  };

  const formatScorecardTarget = (row) => (
    Number.isFinite(row.target)
      ? formatGoalMetricValue(row.target, row.unit, row.formatType)
      : getMissingTargetLabel()
  );

  const safeRollups = {
    kpis: rollups.kpis || {},
    atWorkRate: typeof rollups.atWorkRate === 'number' ? rollups.atWorkRate : 0,
    totalPresent: rollups.totalPresent || 0,
    totalAbsent: rollups.totalAbsent || 0,
    totalLate: rollups.totalLate || 0,
    totalSick: rollups.totalSick || 0,
    totalVacation: rollups.totalVacation || 0,
    eligibleWorkdays: rollups.eligibleWorkdays || 0,
    plannedLeaveDays: rollups.plannedLeaveDays || 0,
    unplannedAbsenceDays: rollups.unplannedAbsenceDays || 0,
    atWorkDays: rollups.atWorkDays || 0,
    vacationTotal: rollups.vacationTotal || 0,
    vacationUsed: rollups.vacationUsed || 0,
    vacationScheduled: rollups.vacationScheduled || 0,
    overallScore: Number.isFinite(rollups.overallScore) ? rollups.overallScore : 0,
    notes: rollups.notes || ''
  };
  const screenClassName = displayMode === 'screen' ? 'scorecard scorecard-screen' : 'scorecard print-only';
  const isScreenMode = displayMode === 'screen';
  const sortedKpis = Object.entries(safeRollups.kpis).sort(([, left], [, right]) => (
    String(left?.label || '').localeCompare(String(right?.label || ''))
  ));
  const screenView = screenScorecardData?.views?.[screenScorecardViewMode] || null;
  const normalizedCardLimit = Math.max(1, Math.min(8, Math.trunc(Number(scorecardCardLimit) || 4)));
  const screenSupportCounts = screenScorecardData?.supportCounts || null;
  const screenLocationOwner = screenScorecardData?.locationOwner || null;
  const managerLocationLabel = manager.locationCode || manager.department || 'Location Rollup';

  const renderInsightList = (items = [], emptyLabel) => {
    if (!items.length) {
      return <span className="scorecard-insight-empty">{emptyLabel}</span>;
    }

    return (
      <ul className="scorecard-insight-list">
        {items.map((item) => (
          <li key={item.kpiKey}>
            <strong>{item.label}</strong>
            <span>{Number.isFinite(item.achievementPercent) ? `${item.achievementPercent.toFixed(1)}% to goal` : item.statusLabel}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderCompactContextChip = (row) => {
    const contextLabels = (row.contextItems || []).map(item => item.label).filter(Boolean);
    const driverLabel = row.detailSummary || row.statusLabel || row.trendLabel;
    const chipSummary = [
      contextLabels.length ? `${contextLabels.length} context` : null,
      driverLabel ? 'drivers' : null
    ].filter(Boolean).join(' + ');

    if (!chipSummary) {
      return null;
    }

    return (
      <details className="scorecard-responsive-detail-chip">
        <summary>{chipSummary}</summary>
        <div className="scorecard-responsive-detail-panel">
          <strong>Operational Context</strong>
          {contextLabels.length ? (
            <ul>
              {contextLabels.map(label => <li key={`${row.kpiKey}-${label}`}>{label}</li>)}
            </ul>
          ) : (
            <p>No additional context.</p>
          )}
          <strong>What Drives It</strong>
          <div>
            <ScorecardRowDetail
              row={row}
              viewMode={screenScorecardViewMode}
            />
          </div>
        </div>
      </details>
    );
  };

  const buildFallbackScreenView = () => ({
    rows: sortedKpis.map(([key, value]) => ({
      kpiKey: key,
      label: value.label || key,
      category: value.category || 'General',
      goalLabel: value.sectionName || value.category || 'Operational Goal',
      unit: value.unit || '',
      target: value.goalValue ?? value.target,
      result: value.actualValue ?? value.current,
      achievementPercent: value.percentToGoal,
      statusTone: getKpiStatusTone(value.status),
      statusLabel: String(value.status || 'no_data').replace('_', ' '),
      trendDirection: 'flat',
      trendLabel: 'No comparison',
      history: Number.isFinite(value.actualValue ?? value.current) ? [value.actualValue ?? value.current] : [],
      locationCode: manager.locationCode || '',
      locationName: manager.department || manager.locationCode || 'Location Rollup',
      managerId: manager.managerDbId || manager.id || null,
      contextItems: []
    })),
    summary: {
      overallScore: safeRollups.overallScore
    },
    insights: {
      underperforming: [],
      biggestDrivers: []
    }
  });

  const renderExportSurface = (activeView, viewMode) => (
    <div className="scorecard-export print-only">
      <div className="scorecard-export-header">
        <div className="scorecard-export-title">
          <span className="scorecard-export-eyebrow">Leadership Scorecard</span>
          <h1>{title || 'Performance Scorecard'}</h1>
          <div className="scorecard-export-meta">
            <span className="scorecard-export-meta-pill">{viewMode === 'ytd' ? 'YTD View' : 'MTD View'}</span>
            <span className="scorecard-export-meta-pill">{manager.locationCode || manager.department || 'Location Rollup'}</span>
            <span className="scorecard-export-meta-pill">Generated {formatDate(new Date())}</span>
          </div>
        </div>
        <div className="scorecard-export-score">
          <small>Overall Score</small>
          <strong>{activeView.summary?.overallScore ?? safeRollups.overallScore}%</strong>
        </div>
      </div>

      <div className="scorecard-export-context">
        <div className="scorecard-export-context-grid">
          <div className="scorecard-export-context-item">
            <span>Employee / View</span>
            <strong>{manager.name}</strong>
          </div>
          <div className="scorecard-export-context-item">
            <span>Location</span>
            <strong>{manager.locationCode || manager.department || 'Location Rollup'}</strong>
          </div>
          <div className="scorecard-export-context-item">
            <span>Owner</span>
            <strong>{screenLocationOwner?.name || 'Unassigned'}</strong>
          </div>
          <div className="scorecard-export-context-item">
            <span>Performance Period</span>
            <strong>{viewMode === 'ytd' ? 'Year-to-Date' : 'Month-to-Date'}</strong>
          </div>
        </div>
      </div>

      <div className="scorecard-export-insights">
        <div className="scorecard-export-insight">
          <span>Underperforming KPIs</span>
          <strong>{activeView.insights?.underperforming?.map((item) => item.label).join(', ') || 'None currently flagged'}</strong>
        </div>
        <div className="scorecard-export-insight">
          <span>Biggest Drivers</span>
          <strong>{activeView.insights?.biggestDrivers?.map((item) => item.label).join(', ') || 'No clear driver signal yet'}</strong>
        </div>
        <div className="scorecard-export-insight">
          <span>Items Needing Attention</span>
          <strong>{screenSupportCounts?.totalPending || 0} pending workflow items</strong>
        </div>
      </div>

      <div className="scorecard-section">
        <h2>{viewMode === 'ytd' ? 'YTD Scorecard' : 'MTD Scorecard'}</h2>
        <table className="kpi-table scorecard-export-table">
          <thead>
            <tr>
              <th>Score status</th>
              <th>Category</th>
              <th>Goal</th>
              <th>KPI</th>
              <th>Target</th>
              <th>Result</th>
              <th>Achievement %</th>
              <th>vs. last period</th>
              <th>Operational Context</th>
            </tr>
          </thead>
          <tbody>
            {activeView.rows.map((row) => (
              <tr key={`print-${row.kpiKey}`} className={row.statusTone}>
                <td>
                  <div className="scorecard-status-indicator">
                    <span className={`scorecard-status-dot ${row.statusTone}`} aria-hidden="true"></span>
                    <span className="scorecard-status-copy">
                      <strong>{getStatusIndicatorLabel(row.statusTone)}</strong>
                      <span>{row.statusLabel}</span>
                    </span>
                  </div>
                </td>
                <td>{row.category}</td>
                <td>{row.goalLabel}</td>
                <td>
                  <strong>{row.label}</strong>
                  <div className="scorecard-kpi-subtle">{row.kpiKey}</div>
                </td>
                <td>{formatScorecardTarget(row)}</td>
                <td>{formatMetricValue(row.result, row.unit, null, row.formatType)}</td>
                <td>{formatPercent(row.achievementPercent)}</td>
                <td>{row.trendDirection === 'up' ? 'Up' : row.trendDirection === 'down' ? 'Down' : 'Stable'} ({row.trendLabel})</td>
                <td>
                  <div className="scorecard-export-context-list">
                    {row.contextItems.map((contextItem) => (
                      <span key={`print-${row.kpiKey}-${contextItem.label}`}>{contextItem.label}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (isScreenMode && !screenScorecardData && !screenScorecardError) {
    return (
      <div className={screenClassName}>
        <div className="scorecard-skeleton">
          <div className="scorecard-skeleton-header">
            <SkeletonBlock width="55%" height={18} />
            <SkeletonBlock width="25%" height={18} style={{ marginTop: 8 }} />
          </div>
          <div className="scorecard-skeleton-insights">
            {[1, 2, 3].map(n => (
              <div key={n} className="scorecard-skeleton-card">
                <SkeletonBlock width="60%" height={14} />
                <SkeletonBlock width="90%" height={10} style={{ marginTop: 8 }} />
                <SkeletonBlock width="75%" height={10} style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
          <div className="scorecard-skeleton-rows">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className="scorecard-skeleton-row">
                <SkeletonBlock width="8%" height={12} />
                <SkeletonBlock width="14%" height={12} />
                <SkeletonBlock width="18%" height={12} />
                <SkeletonBlock width="10%" height={12} />
                <SkeletonBlock width="8%" height={12} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isScreenMode) {
    const activeView = screenView || buildFallbackScreenView();
    const locationGroups = groupScorecardRowsByLocation(activeView.rows, manager);

    return (
      <div className={screenClassName}>
        <div className="scorecard-screen-live">
          <div className="scorecard-screen-header">
            <div className="scorecard-screen-title-block">
              <span className="scorecard-screen-eyebrow">{screenScorecardViewMode === 'ytd' ? 'Year-to-Date Performance' : 'Month-to-Date Performance'}</span>
              {title ? <h2 className="scorecard-screen-heading">{title}</h2> : null}
              <div className="scorecard-screen-meta">
                <span className="scorecard-screen-meta-pill">{manager.name}</span>
                <span className="scorecard-screen-meta-pill">{managerLocationLabel}</span>
                <span className="scorecard-screen-meta-pill">{screenScorecardViewMode === 'ytd' ? 'YTD View' : 'MTD View'}</span>
                {screenLocationOwner?.name ? (
                  <span className="scorecard-screen-meta-pill">Owner {screenLocationOwner.name}</span>
                ) : (
                  <span className="scorecard-screen-meta-pill scorecard-screen-meta-pill-warning">Owner Unassigned</span>
                )}
              </div>
            </div>
            <div className="scorecard-screen-score">
              <small>
                Overall
                <span
                  className="scorecard-score-info"
                  title="Average achievement % across all assigned KPIs. Pending values (no data entered yet) are excluded from the calculation. A score of 100% means every KPI is exactly at its target."
                  aria-label="How is this score calculated?"
                >
                  ?
                </span>
              </small>
              <strong>{activeView.summary?.overallScore ?? safeRollups.overallScore}%</strong>
            </div>
          </div>

          <div className="scorecard-toolbar">
            <div className="scorecard-view-toggle" role="tablist" aria-label="Scorecard view mode">
              <button
                type="button"
                role="tab"
                aria-selected={screenScorecardViewMode === 'mtd'}
                className={screenScorecardViewMode === 'mtd' ? 'is-active' : ''}
                onClick={() => onScreenScorecardViewModeChange('mtd')}
              >
                MTD
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={screenScorecardViewMode === 'ytd'}
                className={screenScorecardViewMode === 'ytd' ? 'is-active' : ''}
                onClick={() => onScreenScorecardViewModeChange('ytd')}
              >
                YTD
              </button>
            </div>

            <div className="scorecard-toolbar-summary">
              <span className="scorecard-inline-pill neutral">
                {activeView.rows.length} KPI rows
              </span>
              {screenSupportCounts ? (
                <>
                  <span className="scorecard-inline-pill fair">
                    {screenSupportCounts.byDomain.goal || 0} goal approvals
                  </span>
                  <span className="scorecard-inline-pill warning">
                    {screenSupportCounts.byDomain.flagged_actual || 0} flagged actuals
                  </span>
                  <span className="scorecard-inline-pill neutral">
                    {screenSupportCounts.byDomain.attendance_event || 0} attendance items
                  </span>
                  <span className="scorecard-inline-pill neutral">
                    {screenSupportCounts.byDomain.performance_impact || 0} performance impact items
                  </span>
                </>
              ) : null}
            </div>
          </div>

          {screenScorecardError ? (
            <div className="scorecard-screen-notice warning">
              {screenScorecardError}
            </div>
          ) : null}

          {activeView.rows.length ? (
            <div className="scorecard-kpi-card-grid" aria-label="Scorecard KPI tiles">
              {activeView.rows.slice(0, normalizedCardLimit).map((row) => (
                <KpiCard
                  key={`card-${row.kpiKey}`}
                  label={row.label}
                  value={formatMetricValue(row.result, row.unit, getPendingReason(row), row.formatType)}
                  goal={formatScorecardTarget(row)}
                  metaLabel={!Number.isFinite(row.target) ? getMissingTargetLabel() : ''}
                  status={row.statusTone}
                  history={row.history || []}
                />
              ))}
            </div>
          ) : null}

          <div className="scorecard-insights-grid">
            <div className="scorecard-insight-card">
              <strong>Underperforming KPIs</strong>
              <p>Metrics currently trailing target and likely to pull the score down.</p>
              {renderInsightList(activeView.insights?.underperforming || [], 'No KPIs are currently flagged as underperforming.')}
            </div>
            <div className="scorecard-insight-card">
              <strong>Biggest Drivers</strong>
              <p>Top KPI contributors based on current achievement percentage.</p>
              {renderInsightList(activeView.insights?.biggestDrivers || [], 'No KPI driver signal is available yet.')}
            </div>
            <div className="scorecard-insight-card">
              <strong>Needs Attention</strong>
              <p>Operational items that may affect score follow-through.</p>
              <ul className="scorecard-insight-list">
                <li>
                  <strong>{screenSupportCounts?.totalPending || 0} pending approvals</strong>
                  <span>Across flagged actuals, goal reviews, attendance events, and performance impacts.</span>
                </li>
                <li>
                  <strong>{activeView.rows.filter((row) => row.statusTone === 'warning').length} KPI rows off track</strong>
                  <span>Review red status indicators first for the fastest score recovery.</span>
                </li>
                <li>
                  <strong>{activeView.rows.filter((row) => row.hasFlaggedActual || row.hasPendingGoalApproval).length} KPI rows with active workflow context</strong>
                  <span>These rows have flagged actuals or pending goal approvals attached.</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="scorecard-location-groups">
            {locationGroups.map((group) => (
              <section className="scorecard-location-group" key={group.key}>
                <div className="scorecard-location-group-header">
                  <div>
                    <span className="scorecard-location-group-eyebrow">Location</span>
                    <h3>{group.label}</h3>
                  </div>
                  <div className="scorecard-location-group-summary">
                    <span>{group.rows.length} KPI{group.rows.length === 1 ? '' : 's'}</span>
                    <div className="scorecard-location-status-bar" aria-label={buildStatusSummaryLabel(group.counts)}>
                      {STATUS_ORDER.map(tone => (
                        <span
                          key={tone}
                          className={`scorecard-location-status-segment ${tone}`}
                          style={{ width: `${getStatusSegmentWidth(group.counts[tone], group.rows.length)}%` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <DataTable
                  tableClassName="kpi-table kpi-table-screen scorecard-data-table"
                  emptyMessage="No KPIs configured for this location."
                  rows={group.rows}
                  rowKey={(row) => `${group.key}:${row.kpiKey}`}
                  rowLabel={(row) => row.label}
                  rowStatus={(row) => row.statusTone}
                  rowClassName={(row) => `scorecard-row ${row.statusTone}`}
                  onRowClick={(row) => {
                    const targetManagerId = row.managerId || manager.managerDbId || manager.id;
                    if (targetManagerId && typeof onManagerDrilldown === 'function') {
                      onManagerDrilldown(targetManagerId, row);
                    }
                  }}
                  renderExpanded={(row, overflowColumns) => {
                    const goalColumn = overflowColumns.find(column => column.key === 'goal');
                    const achievementColumn = overflowColumns.find(column => column.key === 'achievement');
                    const contextColumn = overflowColumns.find(column => column.key === 'context');
                    return (
                      <ScorecardExpandedDetail
                        row={row}
                        viewMode={screenScorecardViewMode}
                        goal={goalColumn ? goalColumn.render(row) : null}
                        achievement={achievementColumn ? achievementColumn.render(row) : null}
                        context={contextColumn ? contextColumn.render(row) : null}
                      />
                    );
                  }}
                  columns={[
                    {
                      key: 'status',
                      label: 'Score status',
                      render: row => (
                        <div className="scorecard-status-indicator">
                          <span className={`scorecard-status-dot ${row.statusTone}`} aria-hidden="true"></span>
                          <span className="scorecard-status-copy">
                            <strong>{getStatusIndicatorLabel(row.statusTone)}</strong>
                            <span>{row.statusLabel}</span>
                          </span>
                        </div>
                      )
                    },
                    { key: 'category', label: 'Category', render: row => row.category },
                    {
                      key: 'kpi',
                      label: 'KPI',
                      className: 'scorecard-kpi-cell',
                      render: row => (
                        <>
                          <strong>{row.label}</strong>
                          <span className="scorecard-kpi-subtle">{row.kpiKey}</span>
                          {renderCompactContextChip(row)}
                        </>
                      )
                    },
                    { key: 'target', label: 'Target', render: row => formatScorecardTarget(row) },
                    { key: 'result', label: 'Result', render: row => formatMetricValue(row.result, row.unit, getPendingReason(row), row.formatType) },
                    { key: 'goal', label: 'Goal', render: row => row.goalLabel },
                    { key: 'achievement', label: 'Achievement %', render: row => formatPercent(row.achievementPercent, getPendingReason(row)) },
                    {
                      key: 'trend',
                      label: 'vs. last period',
                      render: row => (
                        <div className={`scorecard-trend ${row.trendDirection}`}>
                          <span aria-hidden="true">
                            {row.trendDirection === 'up' ? '↑' : row.trendDirection === 'down' ? '↓' : '→'}
                          </span>
                          <span>{row.trendLabel}</span>
                        </div>
                      )
                    },
                    {
                      key: 'context',
                      label: 'Operational Context',
                      className: 'scorecard-col-operational-context',
                      render: row => <ScorecardContextPills row={row} />
                    },
                    {
                      key: 'details',
                      label: 'What Drives It',
                      className: 'scorecard-col-drivers',
                      render: row => (
                        <ScorecardRowDetail
                          row={row}
                          viewMode={screenScorecardViewMode}
                        />
                      )
                    }
                  ]}
                />
              </section>
            ))}
          </div>
        </div>

        {renderExportSurface(activeView, screenScorecardViewMode)}
      </div>
    );
  }

  return (
    <div className={screenClassName}>
      <div className="scorecard-header">
        <div className="header-left">
          <h1 className="scorecard-title">{title}</h1>
          <div className="scorecard-info">
            <p><strong>Manager:</strong> {manager.name}</p>
            <p><strong>Position:</strong> {manager.position}</p>
            <p><strong>Department:</strong> {manager.department}</p>
            <p><strong>Period:</strong> {periodLabel}</p>
            <p><strong>Report Date:</strong> {formatDate(new Date())}</p>
          </div>
        </div>
        <div className="header-right">
          <div className="overall-score">
            <div className="score-circle">
              <span className="score-number">{safeRollups.overallScore}%</span>
              <span className="score-label">Overall Performance</span>
            </div>
          </div>
        </div>
      </div>

      <div className="scorecard-section">
        <h2>Goal vs Actual</h2>
        <table className="kpi-table">
          <thead>
            <tr>
              <th>KPI</th>
              <th>Goal</th>
              <th>Actual</th>
              <th>Variance</th>
              <th>% To Goal</th>
              <th>Rolling Progress</th>
              <th>Score status</th>
            </tr>
          </thead>
          <tbody>
            {sortedKpis.map(([key, value]) => {
              const goalValue = value.goalValue ?? value.target;
              const actualValue = value.actualValue ?? value.current;
              const statusVariant = getKpiStatusTone(value.status);

              return (
                <tr key={key} className={statusVariant}>
                  <td><strong>{value.label || key}</strong></td>
                  <td>{formatGoalMetricValue(goalValue, value.unit, value.formatType)}</td>
                  <td>{formatMetricValue(actualValue, value.unit, null, value.formatType)}</td>
                  <td>{formatMetricValue(value.variance, value.unit, null, value.formatType)}</td>
                  <td className="achievement">{formatPercent(value.percentToGoal)}</td>
                  <td>{formatPercent(value.rollingProgress)}</td>
                  <td>{String(value.status || 'no_data').replace('_', ' ')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ScorecardAttendance rollups={safeRollups} />
      <ScorecardVacation rollups={safeRollups} />

      {displayMode === 'print' ? (
        <>
          <div className="scorecard-section">
            <h2>Notes & Comments</h2>
            <div className="notes-box">
              {safeRollups.notes || 'No notes recorded for this period.'}
            </div>
          </div>

          <div className="scorecard-footer">
            <div className="footer-item">
              <p className="small-text">Reviewed by</p>
              <p><strong>{screenLocationOwner?.name || 'Unassigned'}</strong></p>
              <p className="small-text">Location owner on record</p>
            </div>
            <div className="footer-item">
              <p className="small-text">Review status</p>
              <p><strong>{screenSupportCounts?.totalPending > 0 ? `${screenSupportCounts.totalPending} items pending` : 'No pending workflow items'}</strong></p>
              <p className="small-text">As of {formatDate(new Date())}</p>
            </div>
            <div className="footer-item">
              <p className="small-text">Generated</p>
              <p><strong>{formatDate(new Date())}</strong></p>
              <p className="small-text">Approvals are tracked digitally in the system</p>
            </div>
          </div>

          <p className="print-footer">This is a confidential document. Page 1 of 1</p>
        </>
      ) : null}
    </div>
  );
}

const STATUS_ORDER = ['warning', 'fair', 'good', 'neutral'];

function normalizeScorecardTone(tone) {
  if (tone === 'behind') return 'warning';
  if (tone === 'watch') return 'fair';
  if (tone === 'good') return 'good';
  return tone || 'neutral';
}

function groupScorecardRowsByLocation(rows = [], manager = {}) {
  const groups = new Map();
  const fallbackCode = manager.locationCode || 'ROLLUP';
  const fallbackLabel = manager.locationCode
    ? `${manager.locationCode}${manager.department && manager.department !== manager.locationCode ? ` · ${manager.department}` : ''}`
    : (manager.department || 'Company Rollup');

  rows.forEach(row => {
    const code = row.locationCode || fallbackCode;
    const label = row.locationName || row.locationLabel || (code === fallbackCode ? fallbackLabel : code);
    if (!groups.has(code)) {
      groups.set(code, {
        key: code,
        label,
        rows: [],
        counts: {
          warning: 0,
          fair: 0,
          good: 0,
          neutral: 0
        }
      });
    }

    const group = groups.get(code);
    const tone = normalizeScorecardTone(row.statusTone);
    group.counts[tone] = (group.counts[tone] || 0) + 1;
    group.rows.push({
      ...row,
      statusTone: tone
    });
  });

  return Array.from(groups.values()).sort((left, right) => left.label.localeCompare(right.label));
}

function getStatusSegmentWidth(count, total) {
  if (!count || !total) {
    return 0;
  }
  return Math.max(6, (count / total) * 100);
}

function buildStatusSummaryLabel(counts = {}) {
  return `${counts.warning || 0} behind, ${counts.fair || 0} watch, ${counts.good || 0} healthy, ${counts.neutral || 0} no data`;
}

function ScorecardContextPills({ row }) {
  const items = Array.isArray(row.contextItems) ? row.contextItems : [];
  return (
    <div className="scorecard-context-pills">
      {items.map((contextItem) => (
        <span
          key={`${row.kpiKey}:${contextItem.label}`}
          className={`scorecard-inline-pill ${contextItem.tone}`}
          title={contextItem.navigationHint || 'See the Goals & Performance or Review Requests section to act on this item.'}
        >
          {contextItem.label}
        </span>
      ))}
      {items.length === 0 && (
        <span className="scorecard-context-empty">—</span>
      )}
    </div>
  );
}

function ScorecardRowDetail({ row, viewMode }) {
  const contextItems = Array.isArray(row.contextItems) ? row.contextItems : [];
  return (
    <div className="scorecard-kpi-detail">
      <div className="scorecard-kpi-detail-section">
        <strong>What drives this KPI</strong>
        <p>
          {row.category} metric tracked {viewMode === 'ytd' ? 'year-to-date' : 'month-to-date'}.
          {Number.isFinite(row.target)
            ? ` Target is ${formatGoalMetricValue(row.target, row.unit, row.formatType)}.`
            : ' No goal has been assigned yet — contact an authorized owner or reviewer to set a target.'}
          {Number.isFinite(row.result)
            ? ` Current result is ${formatMetricValue(row.result, row.unit, null, row.formatType)}.`
            : ' No result has been recorded for this period yet.'}
        </p>
      </div>
      {contextItems.length > 0 && (
        <div className="scorecard-kpi-detail-section">
          <strong>Active workflow items ({contextItems.length})</strong>
          <ul className="scorecard-kpi-detail-list">
            {contextItems.map(item => (
              <li key={item.label}>
                <span className={`scorecard-inline-pill ${item.tone}`}>{item.label}</span>
                <span>{item.navigationHint || 'See Goals & Performance or Review Requests to act on this item.'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="scorecard-kpi-detail-section">
        <strong>vs. last period</strong>
        <p>
          {row.trendDirection === 'up' ? 'Improving' : row.trendDirection === 'down' ? 'Declining' : 'Stable'} vs. last period
          {row.trendLabel && row.trendLabel !== 'No comparison' ? ` (${row.trendLabel})` : ''}.
          {row.statusTone === 'warning' ? ' Prioritize this KPI — it is currently pulling down your overall score.' : ''}
        </p>
      </div>
    </div>
  );
}

/**
 * Full-width expanded-row content for the screen/location KPI table.
 *
 * Replaces DataTable's generic overflow-column auto-render (a narrow
 * 2-column grid that squeezed the "What Drives It" cell -- itself a
 * 3-section layout -- into half its own width, and duplicated "vs.
 * last period" as a separate overflow column alongside the copy
 * ScorecardRowDetail already renders). This component owns the whole
 * expanded row instead, laid out as one intentional grid: Goal /
 * Achievement % / Operational Context as compact facts, with the full
 * driver/workflow/trend detail (ScorecardRowDetail, unchanged) given
 * the full row width below them. No information is dropped -- every
 * value previously shown via the overflow columns is still shown
 * here, exactly once.
 */
function ScorecardExpandedDetail({ row, viewMode, goal, achievement, context }) {
  return (
    <div className="scorecard-expanded-detail">
      <div className="scorecard-expanded-detail-fact">
        <strong>Goal</strong>
        <p>{goal}</p>
      </div>
      <div className="scorecard-expanded-detail-fact">
        <strong>Achievement %</strong>
        <p>{achievement}</p>
      </div>
      <div className="scorecard-expanded-detail-fact">
        <strong>Operational Context</strong>
        <div className="scorecard-expanded-detail-context">{context}</div>
      </div>
      <div className="scorecard-expanded-detail-drivers">
        <ScorecardRowDetail row={row} viewMode={viewMode} />
      </div>
    </div>
  );
}

export default Scorecard;
